/**
 * ============================================================================
 * Iron Meridian - Save Game Panel Component
 * ============================================================================
 * 
 * Main menu panel for managing save files.
 * Saves are standalone and named as "Country - Turn X".
 * Displays created date/time and last saved info.
 */

import { useState, useEffect, useCallback } from 'react'
import * as saveService from '../services/saveService.js'
import { ImportExportControls } from './ImportExportControls.jsx'

// ============================================================================
// DATE/TIME FORMATTING
// ============================================================================

function formatDateTime(dateString) {
  if (!dateString) return 'Unknown'
  const date = new Date(dateString)
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatRelativeTime(dateString) {
  if (!dateString) return 'Unknown'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// ============================================================================
// SAVE LIST ITEM
// ============================================================================

function SaveListItem({ save, isSelected, onSelect, onDelete, onExport }) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  const handleDelete = (e) => {
    e.stopPropagation()
    if (showConfirmDelete) {
      onDelete(save.id)
      setShowConfirmDelete(false)
    } else {
      setShowConfirmDelete(true)
    }
  }

  const createdAt = save.metadata?.createdAt || save.createdAt
  const lastSavedAt = save.metadata?.lastSavedAt || save.updatedAt

  return (
    <div
      onClick={() => onSelect(save)}
      className={`p-4 rounded-lg cursor-pointer transition-all border
                 ${isSelected 
                   ? 'bg-green-500/20 border-green-500/50 ring-1 ring-green-500/30' 
                   : 'bg-war-room-700/30 hover:bg-war-room-700/50 border-transparent hover:border-war-room-500/30'}`}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          {/* Save name: Country - Turn X */}
          <div className="text-white font-medium truncate">
            {save.displayName || save.display_name}
          </div>
          
          {/* Turn and status info */}
          <div className="text-gray-400 text-sm mt-1 flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              🔄 Turn {save.turnNumber || save.turn_number}
            </span>
            {save.isAutosave && (
              <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">
                Auto
              </span>
            )}
          </div>
          
          {/* Date info */}
          <div className="text-gray-500 text-xs mt-2 space-y-0.5">
            <div>📅 Created: {formatDateTime(createdAt)}</div>
            <div>💾 Last saved: {formatRelativeTime(lastSavedAt)}</div>
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex flex-col gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onExport(save)
            }}
            className="p-1.5 text-gray-500 hover:text-blue-400 transition-colors"
            title="Export save"
          >
            ⬇
          </button>
          <button
            onClick={handleDelete}
            onBlur={() => setShowConfirmDelete(false)}
            className={`p-1.5 transition-colors ${
              showConfirmDelete 
                ? 'text-red-400 bg-red-500/20 rounded' 
                : 'text-gray-500 hover:text-red-400'
            }`}
            title={showConfirmDelete ? 'Click again to confirm' : 'Delete save'}
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN PANEL
// ============================================================================

export function SaveGamePanel({ onLoadSave, onClose }) {
  // State
  const [saves, setSaves] = useState([])
  const [selectedSave, setSelectedSave] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load saves on mount
  useEffect(() => {
    loadSaves()
  }, [])

  const loadSaves = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await saveService.listSaves()
      setSaves(data)
    } catch (err) {
      setError(`Failed to load saves: ${err.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Save actions
  const handleLoadSave = useCallback(async () => {
    if (!selectedSave) return

    try {
      const saveData = await saveService.loadSave(selectedSave.id)
      if (onLoadSave) {
        onLoadSave(saveData)
      }
    } catch (err) {
      setError(`Failed to load save: ${err.message}`)
    }
  }, [selectedSave, onLoadSave])

  const handleDeleteSave = async (saveId) => {
    try {
      await saveService.deleteSave(saveId)
      setSaves(prev => prev.filter(s => s.id !== saveId))
      if (selectedSave?.id === saveId) {
        setSelectedSave(null)
      }
    } catch (err) {
      setError(`Failed to delete save: ${err.message}`)
    }
  }

  const handleExportSave = async (save) => {
    try {
      const exportData = await saveService.exportSave(save.id)
      const filename = (save.displayName || save.display_name || 'save').replace(/[^a-zA-Z0-9-_]/g, '_')
      saveService.downloadExport(exportData, filename)
    } catch (err) {
      setError(`Failed to export save: ${err.message}`)
    }
  }

  const handleImportComplete = () => {
    loadSaves()
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white flex items-center gap-2">
        <span>💾</span>
        Save Files
      </h2>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">✕</button>
        </div>
      )}

      {/* Save list */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-gray-500 text-center py-8">
            <div className="w-8 h-8 border-2 border-war-room-accent border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            Loading saves...
          </div>
        ) : saves.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            <p className="text-4xl mb-3">💾</p>
            <p>No saves yet</p>
            <p className="text-sm mt-1">Start a new game to create a save</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-1">
            {saves.map(save => (
              <SaveListItem
                key={save.id}
                save={save}
                isSelected={selectedSave?.id === save.id}
                onSelect={setSelectedSave}
                onDelete={handleDeleteSave}
                onExport={handleExportSave}
              />
            ))}
          </div>
        )}

        {/* Load button */}
        {selectedSave && (
          <button
            onClick={handleLoadSave}
            className="w-full px-4 py-3 bg-green-500/20 hover:bg-green-500/30 
                       text-green-400 font-medium rounded-lg transition-colors
                       flex items-center justify-center gap-2"
          >
            <span>▶</span>
            Load "{selectedSave.displayName || selectedSave.display_name}"
          </button>
        )}

        {/* Import section */}
        <div className="pt-4 border-t border-war-room-600/30">
          <h4 className="text-sm font-medium text-gray-400 mb-3">Import Save File</h4>
          <ImportExportControls
            onImportComplete={handleImportComplete}
          />
        </div>
      </div>
    </div>
  )
}

export default SaveGamePanel
